import { useTranslation } from "react-i18next";
import { TrendUp, Info } from "@phosphor-icons/react";

const EARNINGS_IMG_FR = "/earnings-proof.jpg";
const EARNINGS_IMG_EN = "/earnings-proof-en.jpg";

export const EarningsProof = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2);
  const EARNINGS_IMG = lang === "en" ? EARNINGS_IMG_EN : EARNINGS_IMG_FR;

  const stats = [
    { value: t("earnings.stat1_value"), label: t("earnings.stat1_label") },
    { value: t("earnings.stat2_value"), label: t("earnings.stat2_label") },
    { value: t("earnings.stat3_value"), label: t("earnings.stat3_label") },
  ];

  return (
    <section
      data-testid="earnings-block"
      className="relative py-24 md:py-32 bg-slate-50 border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Screenshot side */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative max-w-sm mx-auto">
              <div className="absolute -inset-6 adonis-gradient rounded-3xl opacity-20 blur-3xl" />
              <div className="relative rounded-3xl border border-slate-200 bg-white p-3 shadow-[0_20px_60px_-15px_rgba(15,23,42,0.15)]">
                <div className="rounded-2xl overflow-hidden bg-slate-100">
                  <img
                    src={EARNINGS_IMG}
                    alt="OnlyFans earnings statistics screenshot"
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </div>
              </div>
              {/* Floating badge */}
              <div className="absolute -top-3 -right-3 md:-top-4 md:-right-4 bg-slate-900 text-white rounded-2xl px-3 py-2 shadow-xl flex items-center gap-2 z-10">
                <TrendUp size={16} weight="bold" className="text-emerald-400" />
                <span className="text-xs font-bold">Real back-office</span>
              </div>
            </div>
          </div>

          {/* Text side */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              {t("earnings.overline")}
            </p>
            <h2 className="mt-4 font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              {t("earnings.title")}
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-slate-600 max-w-xl">
              {t("earnings.description")}
            </p>

            {/* Stats */}
            <div className="mt-10 grid grid-cols-3 gap-4">
              {stats.map((s, i) => (
                <div
                  key={i}
                  data-testid={`earnings-stat-${i}`}
                  className="rounded-2xl border border-slate-200 bg-white p-5"
                >
                  <p className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
                    {s.value}
                  </p>
                  <p className="mt-1 text-xs uppercase tracking-wider text-slate-500 font-medium">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div
          data-testid="legal-disclaimer"
          className="mt-16 rounded-2xl border border-amber-200/60 bg-amber-50/40 p-6 flex gap-4"
        >
          <Info
            size={20}
            weight="bold"
            className="shrink-0 mt-0.5 text-amber-600"
          />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700">
              {t("earnings.disclaimer_title")}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              {t("earnings.disclaimer")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EarningsProof;
